import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { useIntl } from "react-intl";
import { v4 } from "uuid";
import { useProperty } from "../../components/property-provider";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import { FormConfiguration } from "../shared/crud/form";
import { SECTION } from "../../lib/constants";
import getTableColumns, { entityToTableRow, UpgradeConfigurationTableRow } from "./tableColumns";
import { upgradeManagerMessages, tableHeaderMessages } from "./upgradeManager.messages";
import { SellingLocationEnum } from "./domain/upgradeConfiguration.schema";
import {
  deleteUpgradeConfiguration,
  findUpgradeConfiguration,
} from "./infrastructure/upgradeConfiguration.api";
import { useHttpClient } from "../../lib/http";
import { useConfig } from "../../lib/config";

/**
 * Selling locations currently active/available for upgrade configurations.
 * When new locations become available (CHECKOUT, BOOKING_CONFIRMATION),
 * add them to this array and the create button will automatically be shown
 * again until each location has a configuration.
 */
const ACTIVE_SELLING_LOCATIONS: SellingLocationEnum[] = [SellingLocationEnum.NOTIFICATION];

const messages = {
  ...defaultCrudMessages,
  ...upgradeManagerMessages,
  tableHeaders: tableHeaderMessages,
};

function UpgradeManagerList() {
  const config = useConfig();
  const httpClient = useHttpClient(config.sellingApiEndpoint);
  const propertyInfo = useProperty();
  const history = useHistory();
  const intl = useIntl();

  const [allLocationsCovered, setAllLocationsCovered] = useState(false);

  const tableColumns = useMemo(() => getTableColumns(intl), [intl]);

  const handleGetAll = useCallback(async () => {
    const items = await findUpgradeConfiguration(httpClient, propertyInfo.ticker);
    const covered = ACTIVE_SELLING_LOCATIONS.every(location =>
      (items || []).some(item => item.sellingLocations?.includes(location))
    );
    setAllLocationsCovered(covered);
    return items.map(entityToTableRow);
  }, [httpClient, propertyInfo.ticker]);

  const handleCreate = useCallback(async () => {
    history.push(`/${propertyInfo.ticker}/upgrade-configuration/new`);
    return "pending";
  }, [history, propertyInfo.ticker]);

  const handleUpdate = useCallback(
    async (row: UpgradeConfigurationTableRow) => {
      history.push(`/${propertyInfo.ticker}/upgrade-configuration/edit/${row.id}`);
    },
    [history, propertyInfo.ticker]
  );

  const handleDelete = useCallback(
    (id: string) => {
      return deleteUpgradeConfiguration(httpClient, id);
    },
    [httpClient]
  );

  const formConfiguration: FormConfiguration<UpgradeConfigurationTableRow> = {
    id: { default: () => v4(), hidden: true },
  };

  return (
    <SimpleCrud<UpgradeConfigurationTableRow>
      managerName={"upgradeManager"}
      formConfiguration={formConfiguration}
      schema={{}}
      tableColumns={tableColumns}
      listHeight={window.innerHeight - 250}
      section={SECTION.UPGRADE_MANAGER}
      onGetAll={handleGetAll}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onCreate={allLocationsCovered ? undefined : handleCreate}
      hideExportButton={true}
      fixedActions={true}
      messages={messages}
    />
  );
}

const WrappedComponent = withAuthorizationVerifier(UpgradeManagerList);
(WrappedComponent as any).defaultProps = {
  witAdminRequired: true,
};

export default WrappedComponent;
